import axios from "axios";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const CRITICAL_ERRORS = [
  "TOKEN_INVALID",
  "NO_TOKEN_PROVIDED",
  "REFRESH_TOKEN_INVALID",
  "REFRESH_TOKEN_EXPIRED_OR_INVALID",
  "REFRESH_TOKEN_MISSING",
];

const SUBSCRIPTION_ERRORS = [
  "SUBSCRIPTION_REQUIRED",
  "NOT_PREMIUM",
  "ACCESS_DENIED",
];

let onSubscriptionRequired = null;

export const setOnSubscriptionRequired = (callback) => {
  onSubscriptionRequired = callback;
};

const refreshToken = async () => {
  const refreshTokenVal = localStorage.getItem("refreshToken");
  if (!refreshTokenVal) return false;
  const SERVER_URL = `https://auth.${process.env.REACT_APP_SITEURL}/auth`;
  try {
    const res = await axios.post(
      `${SERVER_URL}/api/v1/auth/login/getNewTokenCode`,
      { newTokenRequest: refreshTokenVal, device: "leitnerpwa" }
    );
    if (res?.data?.token) {
      localStorage.setItem("token", res.data.token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const logoutUser = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  window.location.reload();
};

const setupInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `bearer ${token}`;
    }
    config.headers.Accept = "Application/json";
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const errorCode = error?.response?.data?.errorCode;

      if (!error.response) {
        return Promise.reject(error);
      }

      if (
        SUBSCRIPTION_ERRORS.includes(errorCode) ||
        (error?.response?.status === 401 && errorCode !== "TOKEN_EXPIRED" && !CRITICAL_ERRORS.includes(errorCode))
      ) {
        if (onSubscriptionRequired) {
          onSubscriptionRequired(error?.response?.data);
        }
        return Promise.reject(error);
      }

      if (originalRequest?._retry) return Promise.reject(error);

      if (errorCode === "TOKEN_EXPIRED") {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `bearer ${token}`;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const success = await refreshToken();
        processQueue(null, localStorage.getItem("token"));
        isRefreshing = false;

        if (success) {
          originalRequest.headers.Authorization = `bearer ${localStorage.getItem("token")}`;
          return instance(originalRequest);
        }

        logoutUser();
        return Promise.reject(error);
      }

      if (CRITICAL_ERRORS.includes(errorCode)) {
        logoutUser();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

const SERVER_AUTH = `https://auth.${process.env.REACT_APP_SITEURL}/auth`;
const SERVER_LEITNER = `https://${process.env.REACT_APP_SITEURL}/leitner`;
const SERVER_SAVEWORD = `https://${process.env.REACT_APP_SITEURL}/userSaveWord`;
const SERVER_PACKAGES = `https://${process.env.REACT_APP_SITEURL}/packages`;

export const authClient = setupInterceptors(axios.create({ baseURL: SERVER_AUTH }));
export const leitnerClient = setupInterceptors(axios.create({ baseURL: SERVER_LEITNER }));
export const saveWordClient = setupInterceptors(axios.create({ baseURL: SERVER_SAVEWORD }));
export const packagesClient = setupInterceptors(axios.create({ baseURL: SERVER_PACKAGES }));

export { refreshToken, logoutUser, CRITICAL_ERRORS };
